import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

type DotsPaginationProps = {
  activeIndex: number
  setActiveIndex: (index: number) => void
  total: number
  playing: boolean
  position?: string
}

export default function DotsPagination({
  activeIndex,
  setActiveIndex,
  total,
  playing,
  position,
}: DotsPaginationProps) {
  const [remainingTime, setRemainingTime] = useState(10000)
  const lastPauseTime = useRef<number | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!playing) return

    const start = lastPauseTime.current ? Date.now() - (10000 - remainingTime) : Date.now()

    const updateRemaining = () => {
      const elapsed = Date.now() - start
      const newTime = Math.max(10000 - elapsed, 0)
      setRemainingTime(newTime)

      if (newTime === 0) {
        setActiveIndex((activeIndex + 1) % total)
        lastPauseTime.current = null
        setRemainingTime(10000)
      }
    }

    intervalRef.current = setInterval(updateRemaining, 100)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      lastPauseTime.current = Date.now()
    }
  }, [playing, setActiveIndex, total, remainingTime, activeIndex])

  useEffect(() => {
    setRemainingTime(10000)
    lastPauseTime.current = null
  }, [activeIndex])

  const handleDotClick = (index: number) => {
    setActiveIndex(index)
    setRemainingTime(10000)
    lastPauseTime.current = null
  }

  return (
    <div
      className={cn(
        'flex items-center',
        'z-20',
        position ?? 'absolute bottom-7 left-7 sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2',
      )}
    >
      {Array.from({ length: total }).map((_, index) => (
        <div
          onClick={() => handleDotClick(index)}
          className="group relative cursor-pointer p-1.5"
          key={index}
        >
          <div
            className={cn(
              'relative h-1.5 rounded-full bg-white/30 transition-all duration-500 ease-in-out',
              index === activeIndex ? 'w-8' : 'w-1.5 group-hover:bg-white/60',
            )}
          >
            {index === activeIndex && (
              <div
                style={{
                  width: `${((10000 - remainingTime) / 10000) * 100}%`,
                }}
                className="absolute left-0 top-0 h-full rounded-full bg-primary-500 shadow-[0_0_4px_4px_rgba(255,52,101,0.3)]"
              />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
